import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ArchiConversation,
  ArchiMessageRow,
  ArchiSidebarMode,
  ArchiUiMessage,
} from "../../../archi/types";

interface ArchiState {
  mode: ArchiSidebarMode;
  conversations: ArchiConversation[];
  conversationsLoading: boolean;
  activeConversationId: number | null;
  /** Messages for the active conversation, in display order. */
  messages: ArchiUiMessage[];
  messagesLoading: boolean;
  /** True while an SSE response is in flight. */
  streaming: boolean;
  /** Last error from the chat surface (display once, then clear). */
  lastError: string | null;
}

const initialState: ArchiState = {
  mode: "collapsed",
  conversations: [],
  conversationsLoading: false,
  activeConversationId: null,
  messages: [],
  messagesLoading: false,
  streaming: false,
  lastError: null,
};

const projectMessageRow = (row: ArchiMessageRow): ArchiUiMessage | null => {
  if (row.role === "user") {
    return {
      clientId: `db-${row.id}`,
      role: "user",
      content: row.content || "",
    };
  }
  if (row.role === "assistant") {
    return {
      clientId: `db-${row.id}`,
      role: "assistant",
      content: row.content || "",
      toolCalls: (row.toolCalls || []).map((tc) => ({
        id: tc.id,
        name: tc.name,
        input: tc.input,
        status: "done",
        // result is filled by following tool row when we project history
      })),
    };
  }
  if (row.role === "tool" && row.toolResults) {
    // Tool rows are merged into the preceding assistant row's toolCalls
    // by `setMessages` below — do not emit a standalone UI row.
    return null;
  }
  return null;
};

const archiSlice = createSlice({
  name: "archi",
  initialState,
  reducers: {
    setMode(state, action: PayloadAction<ArchiSidebarMode>) {
      state.mode = action.payload;
    },
    setConversations(state, action: PayloadAction<ArchiConversation[]>) {
      state.conversations = action.payload;
    },
    setConversationsLoading(state, action: PayloadAction<boolean>) {
      state.conversationsLoading = action.payload;
    },
    upsertConversation(state, action: PayloadAction<ArchiConversation>) {
      const idx = state.conversations.findIndex(
        (c) => c.id === action.payload.id
      );
      if (idx >= 0) state.conversations[idx] = action.payload;
      else state.conversations.unshift(action.payload);
    },
    removeConversation(state, action: PayloadAction<number>) {
      state.conversations = state.conversations.filter(
        (c) => c.id !== action.payload
      );
      if (state.activeConversationId === action.payload) {
        state.activeConversationId = null;
        state.messages = [];
      }
    },
    setActiveConversation(state, action: PayloadAction<number | null>) {
      state.activeConversationId = action.payload;
      state.messages = [];
      state.lastError = null;
    },
    /**
     * Like setActiveConversation but does NOT clear messages.
     * Use this when the backend just created a conversation mid-stream
     * (the `meta` event) — clearing here would wipe the user message
     * and the streaming assistant placeholder, freezing the UI until
     * the user reloads.
     */
    bindActiveConversation(state, action: PayloadAction<number>) {
      state.activeConversationId = action.payload;
    },
    setMessagesLoading(state, action: PayloadAction<boolean>) {
      state.messagesLoading = action.payload;
    },
    /**
     * Replace the whole message list, projecting DB rows into UI rows
     * and merging tool result rows into their preceding assistant rows.
     */
    setMessages(state, action: PayloadAction<ArchiMessageRow[]>) {
      const ui: ArchiUiMessage[] = [];
      let lastAssistant: ArchiUiMessage | null = null;
      for (const row of action.payload) {
        if (row.role === "tool" && row.toolResults && lastAssistant) {
          for (const tr of row.toolResults) {
            const tc = lastAssistant.toolCalls?.find((c) => c.id === tr.toolUseId);
            if (tc) {
              tc.result = { content: tr.content, isError: tr.isError };
              tc.status = tr.isError ? "error" : "done";
            }
          }
          continue;
        }
        const projected = projectMessageRow(row);
        if (!projected) continue;
        ui.push(projected);
        if (projected.role === "assistant") lastAssistant = projected;
      }
      state.messages = ui;
    },
    appendMessage(state, action: PayloadAction<ArchiUiMessage>) {
      state.messages.push(action.payload);
    },
    /** Append a text delta to the latest assistant streaming message. */
    appendAssistantDelta(state, action: PayloadAction<string>) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant" && last.streaming) {
        last.content += action.payload;
      }
    },
    /** Add a tool_use to the current streaming assistant message. */
    appendAssistantToolCall(
      state,
      action: PayloadAction<{ id: string; name: string; input: unknown }>
    ) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant" && last.streaming) {
        last.toolCalls = last.toolCalls || [];
        last.toolCalls.push({ ...action.payload, status: "pending" });
      }
    },
    setToolCallStatus(
      state,
      action: PayloadAction<{
        id: string;
        status: "pending" | "running" | "done" | "error";
        result?: { content: string; isError: boolean };
      }>
    ) {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const m = state.messages[i];
        if (m.role !== "assistant") continue;
        const tc = m.toolCalls?.find((c) => c.id === action.payload.id);
        if (tc) {
          tc.status = action.payload.status;
          if (action.payload.result) tc.result = action.payload.result;
          break;
        }
      }
    },
    finishStreaming(state) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant") last.streaming = false;
      state.streaming = false;
    },
    setStreaming(state, action: PayloadAction<boolean>) {
      state.streaming = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.lastError = action.payload;
    },
    /** Reset to a fresh "new chat" state without changing mode. */
    startNewChat(state) {
      state.activeConversationId = null;
      state.messages = [];
      state.lastError = null;
    },
  },
});

export const {
  setMode,
  setConversations,
  setConversationsLoading,
  upsertConversation,
  removeConversation,
  setActiveConversation,
  bindActiveConversation,
  setMessagesLoading,
  setMessages,
  appendMessage,
  appendAssistantDelta,
  appendAssistantToolCall,
  setToolCallStatus,
  finishStreaming,
  setStreaming,
  setError,
  startNewChat,
} = archiSlice.actions;

export default archiSlice.reducer;
